import React from 'react';
import { styled } from 'baseui';
import { useStyletron } from 'baseui';
import { type BannerOverrides, Banner, KIND as BannerKIND } from 'baseui/banner';
import { Button, KIND, SIZE } from 'baseui/button';
import { Card } from 'baseui/card';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { LabelMedium } from 'baseui/typography';
import type { AnswerT, DynamicAnnotationConfigT, GenericAnnotationT } from '../../types/types';
import { insertIf } from '../../utils/utils';
import QuestionnaireSection from '../questionnaire-section/questionnaire-section';
import { partitionStaticAndDynamicQuestionnaire } from '../utils';
const DynamicAnnotationWrapper = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale100,
  marginBottom: $theme.sizing.scale200
}));
const RangeSelector = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale500
}));
const HeadingWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'space-between'
});
const ActionsWrapper = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: $theme.sizing.scale300,
  marginTop: $theme.sizing.scale0
}));
const HEADING_OVERRIDE = {
  Block: {
    style: ({
      $theme
    }) => ({
      marginTop: $theme.sizing.scale300,
      marginBottom: $theme.sizing.scale300,
      flexGrow: '1'
    })
  }
};
const BANNER_OVERRIDE: BannerOverrides = {
  Root: {
    style: {
      marginLeft: 0,
      marginRight: 0
    }
  },
  Message: {
    style: ({
      $theme
    }) => ({
      ...$theme.typography.ParagraphXSmall
    })
  }
};
type PropsT = {
  annotation: GenericAnnotationT;
  isQuestionnaireComposable?: boolean;
  createNewDynamicQuestionnaire: () => void;
  dynamicAnnotationConfig: DynamicAnnotationConfigT[];
  editableAnnotationId: string;
  handleOnDynamicQuestionnaireChange: (id: string, isAnswerValid: boolean, updatedAnswers?: AnswerT) => void;
  updateFrameStart: (id: string, frameStart: number) => void;
  updateFrameEnd: (id: string, frameEnd: number) => void;
  onRemoveDynamicAttribute: (id: string) => void;
  onSaveDynamicAttribute: (id: string) => void;
  hasDynamicQuestionnaire: boolean;
  showTitleAndActions?: boolean;
};
const DynamicQuestionnaire = ({
  annotation,
  isQuestionnaireComposable,
  createNewDynamicQuestionnaire,
  dynamicAnnotationConfig,
  editableAnnotationId,
  handleOnDynamicQuestionnaireChange,
  updateFrameStart,
  updateFrameEnd,
  onRemoveDynamicAttribute,
  onSaveDynamicAttribute,
  hasDynamicQuestionnaire,
  showTitleAndActions = true
}: PropsT) => {
  const [_, theme] = useStyletron();
  return <>
      {annotation.annotationLabel.questionnaire?.map(questionnaire => <>
          {showTitleAndActions && <HeadingWrapper>
              <LabelMedium overrides={HEADING_OVERRIDE}>Dynamic Attribute</LabelMedium>
              <Button onClick={createNewDynamicQuestionnaire} endEnhancer={<div>+</div>} size={SIZE.xSmall} disabled={!hasDynamicQuestionnaire} kind={KIND.secondary} overrides={{
          BaseButton: {
            style: ({
              $theme
            }) => ({
              paddingTop: '0px',
              paddingBottom: '0px',
              marginTop: $theme.sizing.scale100,
              marginBottom: $theme.sizing.scale100
            })
          }
        }}>
                Add
              </Button>
            </HeadingWrapper>}
          <DynamicAnnotationWrapper>
            <Banner kind={BannerKIND.warning} overrides={BANNER_OVERRIDE}>
              Please track the object for the specified frames to assign dynamic attributes
            </Banner>
            {dynamicAnnotationConfig.map(dynamicAnnotationData => {
          return <Card key={dynamicAnnotationData.id} overrides={{
            Body: {
              style: ({
                $theme
              }) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: $theme.sizing.scale100
              })
            }
          }}>
                  <QuestionnaireSection key={`dynamic-${editableAnnotationId}-${dynamicAnnotationData.annotation.annotationLabel.title}`} questionnaireId={questionnaire.questionnaireId} annotation={dynamicAnnotationData.annotation} questionFilterFn={response => {
              const {
                dynamicQuestionnaire
              } = partitionStaticAndDynamicQuestionnaire(response);
              return dynamicQuestionnaire;
            }} onQuestionnaireChange={(...args) => handleOnDynamicQuestionnaireChange(dynamicAnnotationData.id, ...args)} isQuestionnaireComposable={isQuestionnaireComposable} skipHeading />
                  <RangeSelector>
                    <div>
                      <FormControl label="Start">
                        <Input value={dynamicAnnotationData.frameStart} onChange={e => updateFrameStart(dynamicAnnotationData.id, Number(e.target.value))} placeholder="Start frame" type="number" overrides={{
                    Input: {
                      style: ({
                        $theme
                      }) => ({
                        fontSize: $theme.sizing.scale550,
                        paddingLeft: $theme.sizing.scale300,
                        paddingRight: $theme.sizing.scale300
                      })
                    }
                  }} clearable clearOnEscape />
                      </FormControl>
                    </div>
                    <div>
                      <FormControl label="End">
                        <Input value={dynamicAnnotationData.frameEnd} onChange={e => updateFrameEnd(dynamicAnnotationData.id, Number(e.target.value))} placeholder="End frame" type="number" overrides={{
                    Input: {
                      style: ({
                        $theme
                      }) => ({
                        fontSize: $theme.sizing.scale550,
                        paddingLeft: $theme.sizing.scale300,
                        paddingRight: $theme.sizing.scale300
                      })
                    }
                  }} clearable clearOnEscape />
                      </FormControl>
                    </div>
                  </RangeSelector>
                  <ActionsWrapper>
                    <Button onClick={() => {
                onRemoveDynamicAttribute(dynamicAnnotationData.id);
              }} overrides={{
                BaseButton: {
                  style: () => ({
                    width: '100%'
                  })
                }
              }} kind={KIND.secondary} size={SIZE.compact}>
                      Cancel
                    </Button>
                    <Button onClick={() => {
                onSaveDynamicAttribute(dynamicAnnotationData.id);
              }} size={SIZE.compact} overrides={{
                BaseButton: {
                  style: () => ({
                    width: '100%',
                    ...insertIf({
                      backgroundColor: theme.colors.backgroundPositive
                    }, dynamicAnnotationData.savedToStore)
                  })
                }
              }}>
                      Save
                    </Button>
                  </ActionsWrapper>
                </Card>;
        })}
          </DynamicAnnotationWrapper>
        </>)}
    </>;
};
export default DynamicQuestionnaire;